/**
 * One-shot recordings in velocity LAYERS: the piano (public/piano/), the
 * archtop guitar and the upright bass share this loader. A set is a folder holding a manifest
 * `{ layers: [{ upTo? }…], samples: [{ file, midi, layer }] }` and its audio
 * files. Every file is levelled to one register curve when the set is built,
 * so a layer is a TIMBRE and the voice's gain is the loudness: the gain picks
 * the layer, and the caller scales the level by the same gain.
 *
 * Loading is progressive. The loudest layer — what full gain plays: held
 * chords, taps, accents — loads first and `load()` resolves on it; the softer
 * layers follow in the background, and until one lands the nearest loaded
 * layer answers for it. Failure is silent and final for the session: the
 * caller's synthesized voice simply carries on.
 */

/** One decoded recording: the pitch it sounds at rate 1, where in the buffer
 * the note actually starts, and how long after that its attack is heard. */
export interface LayeredSample {
  base: number;
  buffer: AudioBuffer;
  offset: number;
  /** Seconds from `offset` to where the attack is heard (`sampleAttackSeconds`);
   * a voice that times its notes by ear starts the recording this much early. */
  attack: number;
}

export interface SampleManifest {
  layers: { upTo?: number }[];
  samples: { file: string; midi: number; layer: number }[];
}

/** Read a set's manifest: `{ layers, samples }`, or the bare list of
 * `{ file, midi }` a single-layer set writes. Malformed entries are dropped. */
export function parseSampleManifest(raw: unknown): SampleManifest {
  const source = (Array.isArray(raw) ? { samples: raw } : (raw ?? {})) as {
    layers?: unknown;
    samples?: unknown;
  };
  const layers = Array.isArray(source.layers)
    ? source.layers.map((layer) => {
        const upTo = (layer as { upTo?: unknown } | null)?.upTo;
        return typeof upTo === 'number' && Number.isFinite(upTo) ? { upTo } : {};
      })
    : [];
  if (!layers.length) layers.push({});
  const samples = (Array.isArray(source.samples) ? source.samples : []).flatMap((entry) => {
    const {
      file,
      midi,
      layer = 0,
    } = (entry ?? {}) as {
      file?: unknown;
      midi?: unknown;
      layer?: unknown;
    };
    return typeof file === 'string' &&
      Number.isInteger(midi) &&
      Number.isInteger(layer) &&
      (layer as number) >= 0 &&
      (layer as number) < layers.length
      ? [{ file, midi: midi as number, layer: layer as number }]
      : [];
  });
  return { layers, samples };
}

/**
 * Where the note starts in a decoded recording: the first frame louder than
 * 2% of the attack's peak, less 2ms so the attack's front edge survives. The
 * files are trimmed to start just before it, but an MP3 decoder may or may
 * not strip the encoder's priming (~25ms of silence), and a chord whose notes
 * speak 25ms late reads as laggy — so the onset is found where it actually is.
 */
export function sampleOnsetSeconds(buffer: AudioBuffer): number {
  const frames = Math.min(buffer.length, Math.round(buffer.sampleRate * 0.5));
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) =>
    buffer.getChannelData(c),
  );
  let peak = 0;
  for (const data of channels) {
    for (let i = 0; i < frames; i += 1) peak = Math.max(peak, Math.abs(data[i]));
  }
  if (peak === 0) return 0;
  for (let i = 0; i < frames; i += 1) {
    for (const data of channels) {
      if (Math.abs(data[i]) > 0.02 * peak) return Math.max(0, i / buffer.sampleRate - 0.002);
    }
  }
  return 0;
}

/**
 * How long after `offset` a recording's attack is HEARD: the time its first
 * 15 ms take to reach half their peak, in 1 ms steps. A plucked string speaks
 * within a few milliseconds (Meatbass's upright: 2–8 ms); the window stops
 * before the body blooms, which on a low bass string peaks 30–100 ms in and
 * would put the onset at the bloom instead of the pluck.
 */
export function sampleAttackSeconds(buffer: AudioBuffer, offset: number): number {
  const rate = buffer.sampleRate;
  const step = Math.max(1, Math.round(rate / 1000));
  const from = Math.max(0, Math.round(offset * rate));
  const to = Math.min(buffer.length, from + Math.round(rate * 0.015));
  if (to <= from) return 0;
  const channels = Array.from({ length: buffer.numberOfChannels }, (_, c) =>
    buffer.getChannelData(c),
  );
  const envelope: number[] = [];
  for (let start = from; start < to; start += step) {
    let level = 0;
    for (const data of channels) {
      for (let i = start; i < Math.min(to, start + step); i += 1) {
        level = Math.max(level, Math.abs(data[i]));
      }
    }
    envelope.push(level);
  }
  const peak = Math.max(...envelope);
  if (!(peak > 0)) return 0;
  return (envelope.findIndex((level) => level >= peak / 2) * step) / rate;
}

export class LayeredSampler {
  /** Per layer, per recorded pitch: its round robins, in manifest order. */
  private layers: { upTo?: number; notes: Map<number, (LayeredSample | undefined)[]> }[] = [];
  private loading: Promise<void> | null = null;
  private settled: Promise<void> | null = null;
  private expected = 0;
  private loaded = 0;
  /** Which round robin each (layer, pitch) plays next. */
  private turns = new Map<string, number>();

  /**
   * @param folder the set's folder under the deployment base ('piano', …)
   * @param maxDistance how far (semitones) a recording may be pitch-shifted;
   *   beyond it the layer is treated as missing the note
   */
  constructor(
    private readonly folder: string,
    private readonly maxDistance = 2,
  ) {}

  /** Fetch + decode once; safe to call repeatedly. Resolves when the loudest
   * layer is in (or the set proved unavailable). */
  load(context: BaseAudioContext, baseUrl = import.meta.env.BASE_URL ?? '/'): Promise<void> {
    if (!this.loading) {
      let settle!: () => void;
      this.settled = new Promise<void>((resolve) => {
        settle = resolve;
      });
      this.loading = (async () => {
        try {
          const root = `${baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`}${this.folder}/`;
          const manifest = parseSampleManifest(await (await fetch(`${root}manifest.json`)).json());
          this.layers = manifest.layers.map((layer) => ({ ...layer, notes: new Map() }));
          this.expected = manifest.samples.length;
          // Slots are laid out up front, so the round robins keep their
          // manifest order however the fetches happen to finish.
          const slots = manifest.samples.map((entry) => {
            const notes = this.layers[entry.layer].notes;
            const variants = notes.get(entry.midi) ?? [];
            notes.set(entry.midi, variants);
            variants.push(undefined);
            return { entry, variants, index: variants.length - 1 };
          });
          const loadLayer = (layer: number) =>
            Promise.all(
              slots
                .filter((slot) => slot.entry.layer === layer)
                .map(async ({ entry, variants, index }) => {
                  try {
                    const bytes = await (await fetch(`${root}${entry.file}`)).arrayBuffer();
                    const buffer = await context.decodeAudioData(bytes);
                    const offset = sampleOnsetSeconds(buffer);
                    variants[index] = {
                      base: entry.midi,
                      buffer,
                      offset,
                      attack: sampleAttackSeconds(buffer, offset),
                    };
                    this.loaded += 1;
                  } catch {
                    /* one missing note falls back; the rest still load */
                  }
                }),
            );
          const loudestFirst = this.layers.map((_, index) => index).reverse();
          await loadLayer(loudestFirst[0]);
          void loudestFirst
            .slice(1)
            .reduce<Promise<unknown>>(
              (chain, index) => chain.then(() => loadLayer(index)),
              Promise.resolve(),
            )
            .then(settle, settle);
        } catch {
          /* no manifest / no network: the caller's synth carries the session */
          settle();
        }
      })();
    }
    return this.loading;
  }

  /** Resolves once every layer has loaded or failed — for a set small enough
   * to wait for whole (call `load` first; before it, resolves at once). */
  whenSettled(): Promise<void> {
    return this.settled ?? Promise.resolve();
  }

  /** Every file the manifest lists has loaded (auditions that must not fall
   * back to a substitute voice ask this). */
  isComplete(): boolean {
    return this.expected > 0 && this.loaded === this.expected;
  }

  /** The recording nearest `midi` in the layer `gain` asks for — or, while
   * that layer is missing the note, in the nearest layer that has it. A
   * pitch recorded more than once answers with its round robins in turn. */
  nearest(midi: number, gain = 1): LayeredSample | null {
    const layers = this.layers;
    if (!layers.length) return null;
    let wanted = layers.findIndex((layer) => layer.upTo === undefined || gain < layer.upTo);
    if (wanted < 0) wanted = layers.length - 1;
    for (let step = 0; step < layers.length * 2; step += 1) {
      // wanted, wanted+1, wanted-1, wanted+2, …
      const index = wanted + (step % 2 ? (step + 1) / 2 : -step / 2);
      if (index < 0 || index >= layers.length) continue;
      let bestBase: number | null = null;
      let best: LayeredSample[] = [];
      for (const [base, variants] of layers[index].notes) {
        const ready = variants.filter((sample): sample is LayeredSample => !!sample);
        if (!ready.length) continue;
        if (bestBase === null || Math.abs(base - midi) < Math.abs(bestBase - midi)) {
          bestBase = base;
          best = ready;
        }
      }
      if (bestBase === null || Math.abs(bestBase - midi) > this.maxDistance) continue;
      const key = `${index}:${bestBase}`;
      const turn = this.turns.get(key) ?? 0;
      this.turns.set(key, turn + 1);
      return best[turn % best.length];
    }
    return null;
  }
}
